import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, Building2, CalendarDays,
} from 'lucide-react';
import DashboardSidebar from '../../../agencyDashboardSidebar';
import { getJobById, getJobMetadata } from '../../../../../../services/staffingService';
import CoverageBar from '../../shared/CoverageBar';
import ShiftsAssignTabMaster from './allocation/ShiftsAssignTabMaster';

const STATUS_CONFIG = {
  open:      { label: 'Open',      colors: 'bg-red-50 text-red-700 border-red-200'         },
  disputed:  { label: 'Disputed',  colors: 'bg-amber-50 text-amber-700 border-amber-200'   },
  completed: { label: 'Completed', colors: 'bg-gray-50 text-gray-600 border-gray-200'      },
  paid:      { label: 'Paid',      colors: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const JobDispatchDetail = () => {
  const { providerCompanyId, jobId } = useParams();
  const history  = useHistory();

  const [showSidebar, setShowSidebar] = useState(false);
  const [job, setJob]                 = useState(null);
  const [shifts, setShifts]           = useState([]);
  const [offersMetadata, setOffersMetadata] = useState([]);
  const [loading, setLoading]         = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [jobResp, metaResp] = await Promise.all([
        getJobById(providerCompanyId, jobId),
        getJobMetadata(providerCompanyId, jobId),
      ]);
      setJob(jobResp.data.job);
      setShifts(jobResp.data.shifts || []);
      setOffersMetadata(metaResp.data.offers || []);
    } catch (err) {
      console.error('[JobDispatchDetail] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [providerCompanyId, jobId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const customerName = job?.customer_company_id?.companyFriendlyName
    || job?.customer_company_id?.companyLegalName
    || 'Customer';

  const stats   = job?.statistics || {};
  const covered = stats.totalShiftsCovered ?? 0;
  const total   = stats.totalStaffRequired ?? shifts.reduce((s, sh) => s + (sh.staffsRequired || 1), 0);
  const statusInfo = STATUS_CONFIG[job?.status] || STATUS_CONFIG.open;

  return (
    <div className="flex h-screen overflow-hidden bg-background font-inter">
      <DashboardSidebar isOpen={showSidebar} setIsOpen={setShowSidebar} />

      <main className="flex-1 overflow-y-auto">
        <div className="p-6">

          {/* Back + Header */}
          <div className="mb-6">
            <button
              onClick={() => history.push(`/provider-company/${providerCompanyId}/dispatch`)}
              className="mb-3 flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft size={16} />
              Back to Dispatch
            </button>

            {loading ? (
              <div className="space-y-2">
                <div className="h-7 w-48 animate-pulse rounded bg-muted" />
                <div className="h-4 w-64 animate-pulse rounded bg-muted" />
              </div>
            ) : (
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                      <Briefcase size={18} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-foreground">{customerName}</h2>
                      <p className="text-xs font-mono text-muted-foreground">
                        #{jobId?.slice(-8).toUpperCase()}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    {/* Status */}
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${statusInfo.colors}`}>
                      {statusInfo.label}
                    </span>
                    {/* Coverage */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Coverage:</span>
                      <div className="w-28">
                        <CoverageBar covered={covered} total={total} />
                      </div>
                    </div>
                    {/* Shifts count */}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <CalendarDays size={13} />
                      {total} shift{total !== 1 ? 's' : ''}
                    </div>
                    {/* Service type */}
                    {job?.serviceType && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Building2 size={13} />
                        {job.serviceType}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-border">
            <div className="flex gap-1">
              <h3 className="px-4 py-2.5 text-sm font-medium text-brand-pink">Assign Officers to Shifts</h3>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded-xl bg-muted" />
              ))}
            </div>
          ) : (
            <ShiftsAssignTabMaster
              job={job}
              shifts={shifts}
              offersMetadata={offersMetadata}
              onRefresh={fetchAll}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default JobDispatchDetail;
